import { inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AppState, CompanyService, Dialog, DocumentService, PreviewDocument } from '../common';
import { AppAuthService } from '../common/auth';
import { NotificationService } from '../common/service/notification.service';
import { Document } from '../model';
import { DocumentMetadata } from './document-metadata.model';
import { formatJsonDate } from '../util';
import { DocumentUploadedSortField } from './document-list-table.component';
import { ManagedDocument } from '../common/models/document.model';

@Injectable({ providedIn: 'root' })
export class RemoteDocumentService {
  private readonly documentService = inject(DocumentService);
  private readonly companyService = inject(CompanyService);
  private readonly appState = inject(AppState);
  private readonly authService = inject(AppAuthService);
  private readonly dialogService = inject(Dialog);
  private readonly notification = inject(NotificationService);

  readonly remoteDocuments = signal<Document[]>([]);
  readonly remotePreview = signal<Record<string, PreviewDocument>>({});
  readonly remotePageIndex = signal(0);
  readonly remotePageSize = signal(20);
  readonly remoteTotalElements = signal(0);
  readonly remoteSortField = signal<DocumentUploadedSortField>('date');
  readonly remoteSortDirection = signal<'asc' | 'desc'>('desc');
  readonly remoteLoading = signal(false);

  async loadRemoteDocuments(page = this.remotePageIndex(), size = this.remotePageSize()): Promise<void> {
    const companyId = this.appState.companySite$.value?.company?.id;
    if (!companyId) {
      this.remoteDocuments.set([]);
      this.remotePreview.set({});
      this.remoteTotalElements.set(0);
      this.remotePageIndex.set(0);
      return;
    }

    const query: Record<string, any> = { page, size };
    const sortField = this.remoteSortField();
    const sortDirection = this.remoteSortDirection();
    query['sort'] = `${sortField},${sortDirection}`;

    this.remoteLoading.set(true);
    try {
      const pageData = await this.companyService.allDocumentsForCompanyTeam(companyId, query);
      const docs = pageData?.content ?? [];
      this.remoteDocuments.set(docs);
      this.remoteTotalElements.set(pageData?.totalElements ?? docs.length);
      this.remotePageIndex.set(pageData?.pageNumber ?? page);
      this.remotePageSize.set(pageData?.pageSize ?? size);
      const ids = docs.map(({ id }) => id);
      this.remotePreview.set(ids.length ? await this.documentService.previewList(ids) : {});
    } catch (error) {
      console.error('Failed to load uploaded documents', error);
      this.remoteDocuments.set([]);
      this.remotePreview.set({});
      this.remoteTotalElements.set(0);
      throw error;
    } finally {
      this.remoteLoading.set(false);
    }
  }

  async viewRemoteDocument(document: Document): Promise<void> {
    try {
      const response = await this.documentService.view(document.id, undefined, {
        name: document.title ?? document.fileName ?? document.id,
      });
      if (!response?.body) {
        return;
      }
      const { body } = response;
      await this.dialogService.showPdf(document.title ?? 'Document', body, {
        fileName: document.fileName ?? `${document.title ?? 'document'}.pdf`,
        mimeType: document.fileType ?? body.type,
      });
    } catch (error) {
      console.error('Failed to view document', error);
      const message = await this.extractErrorMessage(error, document);
      await this.notification.errorWithSupport(message, undefined, { subject: 'document_access_denied_tr' });
    }
  }

  async editUploadedDocument(
    document: Document,
    options?: { enforcedCategory?: 'company' | 'chain' | 'report' | 'attributes' | null }
  ): Promise<boolean> {
    let previewUrl: string | null = null;
    try {
      const response = await this.documentService.view(document.id, undefined, {
        name: document.title ?? document.fileName ?? document.id,
      });
      const previewBlob = response?.body ?? null;
      previewUrl = previewBlob ? URL.createObjectURL(previewBlob) : null;
      const workbenchDocument: ManagedDocument = {
        id: document.id,
        title: document.title ?? document.fileName ?? document.id,
        mimeType: previewBlob?.type ?? document.fileType ?? 'application/pdf',
        extension: 'pdf',
        kind: 'pdf',
        blob: previewBlob ?? undefined,
        createdAt: document.date ?? new Date().toISOString(),
        size: previewBlob?.size ?? 0,
        pages: document.pages,
        documentTypeId: document.type?.id,
        documentTypeName: document.type?.name,
        documentDate: document.date,
      };
      const { DocumentWorkbenchDialogComponent } = await import('./document-workbench-dialog.component');
      const result = await this.dialogService.open<any>(DocumentWorkbenchDialogComponent, {
        width: '100vw',
        maxWidth: '100vw',
        height: '100dvh',
        maxHeight: '100dvh',
        panelClass: 'document-workbench-dialog',
        autoFocus: false,
        data: {
          mode: 'split',
          document: workbenchDocument,
          pdfDataUrl: previewUrl ?? undefined,
          actionLabels: 'edit',
          secondaryActionBehavior: 'close',
          documentTypeCategory: options?.enforcedCategory ?? null,
          showSuggestedTypeHint: false,
          showQualityWarning: false,
          suggestedDocumentTypeId: null,
          suggestedDocumentTypeName: null,
          suggestedDocumentTypeConfidence: null,
          suggestionError: false,
          suggestionErrorMessage: null,
          readOnlyPreview: false,
          hideSplitActions: true,
        },
      });
      if (!result || result.kind !== 'metadata' || result.action !== 'save') {
        return false;
      }
      const metadata = result.metadata as DocumentMetadata;
      const formattedDate = metadata.documentDate ? formatJsonDate(metadata.documentDate) : undefined;
      const updated: Document = {
        ...document,
        title: metadata.title ?? document.title,
        type: metadata.documentTypeId
          ? {
              id: metadata.documentTypeId,
              name: metadata.documentTypeName ?? document.type?.name ?? '',
            }
          : document.type,
        date: formattedDate ?? document.date,
      };
      await this.documentService.update(document.id, updated);
      this.remoteDocuments.update((docs) => docs.map((doc) => (doc.id === document.id ? updated : doc)));
      await this.notification.success('Document_details_saved_tr');
      return true;
    } catch (error) {
      console.error('Failed to update uploaded document', error);
      throw error;
    } finally {
      if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    }
  }

  async deleteUploadedDocument(document: Document): Promise<void> {
    try {
      await this.documentService.delete(document);
      await this.notification.success('Document_removed_tr');
      await this.loadRemoteDocuments(this.remotePageIndex(), this.remotePageSize());
    } catch (error) {
      console.error('Failed to delete uploaded document', error);
      throw error;
    }
  }

  private async extractErrorMessage(error: unknown, document: Document): Promise<string> {
    const fallback = 'Failed_to_open_document_tr';
    if (!(error instanceof HttpErrorResponse)) {
      return fallback;
    }
    const payload = await this.extractErrorPayload(error);
    const rawMessage = payload?.message ?? payload?.error ?? error.message;
    if (typeof rawMessage === 'string' && rawMessage.trim()) {
      return this.rewriteNoAccessMessage(rawMessage, document) ?? rawMessage;
    }
    return fallback;
  }

  private rewriteNoAccessMessage(rawMessage: string, document: Document): string | null {
    const isNoAccess = /no access to document\s+\S+\s+for user\s+\S+/i.test(rawMessage);
    if (!isNoAccess) {
      return null;
    }
    const documentName = document.title?.trim() || document.fileName?.trim() || document.id;
    const userName = this.resolveUserDisplayName();
    if (userName) {
      return `No access to document "${documentName}" for user ${userName}.`;
    }
    return `No access to document "${documentName}".`;
  }

  private resolveUserDisplayName(): string | null {
    const user = this.authService.user;
    if (!user) {
      return null;
    }
    if (user.impersonated?.trim()) {
      return `${user.impersonated} (for ${user.email})`;
    }
    return user.email?.trim() || null;
  }

  private async extractErrorPayload(error: HttpErrorResponse): Promise<any> {
    if (error.error === undefined || error.error === null) {
      return undefined;
    }
    if (error.error instanceof Blob) {
      try {
        const text = await error.error.text();
        return JSON.parse(text);
      } catch {
        return undefined;
      }
    }
    if (typeof error.error === 'string') {
      try {
        return JSON.parse(error.error);
      } catch {
        return { message: error.error };
      }
    }
    return error.error;
  }
}
